import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Calendar, ShoppingCart, Loader2, ChefHat, Edit2, Trash2, Share2, Printer, History, Plus, Minus, X, User, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import NavigationMenu from '../components/NavigationMenu';
import EditMealDialog from '../components/EditMealDialog';
import SharePlanDialog from '../components/SharePlanDialog';
import { useAuth } from '../contexts/AuthContext';
import '../print.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MealPlannerPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [dietTypes, setDietTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressStep, setProgressStep] = useState('');
  const [days, setDays] = useState(7);
  const [preferences, setPreferences] = useState('');
  const [dietType, setDietType] = useState('');
  const [mealPlan, setMealPlan] = useState(null);
  const [savedPlans, setSavedPlans] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [editingMeal, setEditingMeal] = useState(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [newShoppingItem, setNewShoppingItem] = useState({ item: '', quantity: '', category: 'Outros' });

  useEffect(() => {
    fetchDietTypes();
    fetchSavedPlans();
  }, []);

  const fetchDietTypes = async () => {
    try {
      const response = await axios.get(`${API}/diet-types`);
      setDietTypes(response.data.diet_types);
    } catch (error) {
      console.error('Error fetching diet types:', error);
    }
  };

  const fetchSavedPlans = async () => {
    try {
      const response = await axios.get(`${API}/meal-plans`);
      setSavedPlans([...response.data.my_plans, ...response.data.shared_plans]);
    } catch (error) {
      console.error('Error fetching saved plans:', error);
    }
  };

  const handleDeletePlan = async (planId) => {
    try {
      await axios.delete(`${API}/meal-plans/${planId}`);
      toast.success(t('mealPlanner.planDeleted'));
      await fetchSavedPlans();
      if (currentPlanId === planId) {
        setMealPlan(null);
        setCurrentPlanId(null);
      }
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast.error(t('common.error'));
    }
  };

  const handleLoadPlan = async (planId) => {
    try {
      const response = await axios.get(`${API}/meal-plans/${planId}`);
      setMealPlan(response.data);
      setCurrentPlanId(planId);
      setShowHistory(false);
      toast.success(t('common.loading'));
    } catch (error) {
      console.error('Error loading plan:', error);
      toast.error(t('common.error'));
    }
  };

  const handleUpdateMealPlan = async (updatedMealPlan) => {
    try {
      await axios.put(`${API}/meal-plans/${currentPlanId}`, {
        meal_plan: updatedMealPlan
      });
      setMealPlan(prev => ({ ...prev, meal_plan: updatedMealPlan }));
      toast.success(t('mealPlanner.planSaved'));
    } catch (error) {
      console.error('Error updating meal plan:', error);
      toast.error(t('common.error'));
    }
  };

  const handleUpdateShoppingList = async (updatedList) => {
    try {
      await axios.put(`${API}/meal-plans/${currentPlanId}`, {
        shopping_list: updatedList
      });
      setMealPlan(prev => ({ ...prev, shopping_list: updatedList }));
      toast.success(t('common.loading'));
    } catch (error) {
      console.error('Error updating shopping list:', error);
      toast.error(t('common.error'));
    }
  };

  const handleAddShoppingItem = () => {
    if (!newShoppingItem.item || !newShoppingItem.quantity) {
      toast.error(t('common.error'));
      return;
    }
    
    const updatedList = [...(mealPlan.shopping_list || []), newShoppingItem];
    handleUpdateShoppingList(updatedList);
    setNewShoppingItem({ item: '', quantity: '', category: 'Outros' });
  };

  const handleRemoveShoppingItem = (index) => {
    const updatedList = mealPlan.shopping_list.filter((_, i) => i !== index);
    handleUpdateShoppingList(updatedList);
  };

  const handleEditMeal = (dayIndex, mealType, currentMeal) => {
    setEditingMeal({ dayIndex, mealType, currentMeal });
  };

  const handleSaveMeal = async (mealData) => {
    if (!editingMeal) return;
    
    const updatedPlan = [...mealPlan.meal_plan];
    const dayIndex = editingMeal.dayIndex;
    const mealType = editingMeal.mealType;
    
    updatedPlan[dayIndex][mealType] = mealData.meal;
    updatedPlan[dayIndex][`${mealType}_calories`] = mealData.calories;
    updatedPlan[dayIndex][`${mealType}_protein`] = mealData.protein;
    
    // Recalculate day totals
    const day = updatedPlan[dayIndex];
    day.total_calories = 
      (day.breakfast_calories || 0) + 
      (day.lunch_calories || 0) + 
      (day.dinner_calories || 0) + 
      (day.snacks_calories || 0);
    day.total_protein = 
      (day.breakfast_protein || 0) + 
      (day.lunch_protein || 0) + 
      (day.dinner_protein || 0) + 
      (day.snacks_protein || 0);
    
    await handleUpdateMealPlan(updatedPlan);
    setEditingMeal(null);
  };

  const handlePrint = () => {
    if (!currentPlanId) {
      toast.error(t('common.error'));
      return;
    }
    
    // Open PDF in new tab
    const pdfUrl = `${API}/meal-plans/${currentPlanId}/pdf`;
    window.open(pdfUrl, '_blank');
    toast.success(t('mealPlanner.downloadPDF'));
  };

  const handleClonePlan = async (planToClone) => {
    try {
      setLoading(true);
      
      // Create a copy of the plan
      const saveResponse = await axios.post(`${API}/meal-plans`, {
        days: planToClone.days,
        diet_type: planToClone.diet_type,
        preferences: planToClone.preferences,
        meal_plan: planToClone.meal_plan,
        shopping_list: planToClone.shopping_list,
        prep_tips: planToClone.prep_tips,
        total_calories: planToClone.total_calories,
        total_protein: planToClone.total_protein
      });
      
      // Load the cloned plan
      const response = await axios.get(`${API}/meal-plans/${saveResponse.data.id}`);
      setMealPlan(response.data);
      setCurrentPlanId(saveResponse.data.id);
      setShowHistory(false);
      await fetchSavedPlans();
      
      toast.success(t('mealPlanner.planCloned'));
    } catch (error) {
      console.error('Error cloning plan:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setProgress(0);
      
      // Passo 1: Analisando preferências
      setProgressStep(t('mealPlanner.analyzingPreferences'));
      setProgress(15);
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Passo 2: Selecionando receitas
      setProgressStep(t('mealPlanner.selectingRecipes'));
      setProgress(30);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Passo 3: Criando plano
      setProgressStep(t('mealPlanner.creatingPlan'));
      setProgress(50);
      
      const response = await axios.post(`${API}/ai/meal-plan`, {
        days,
        preferences: preferences || null,
        diet_type: dietType || null
      }, { timeout: 90000 });
      
      // Passo 4: Calculando nutrição
      setProgressStep(t('mealPlanner.calculatingNutrition'));
      setProgress(70);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Passo 5: Gerando lista de compras
      setProgressStep(t('mealPlanner.generatingShoppingList'));
      setProgress(80);
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Passo 6: Finalizando
      setProgressStep(t('mealPlanner.finalizingPlan'));
      setProgress(95);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setProgress(100);
      setMealPlan(response.data);
      setCurrentPlanId(null); // Reset plan ID (not saved yet)
      toast.success('✨ ' + t('mealPlanner.planCreatedSave'));
      
      setProgress(100);
      setMealPlan(response.data);
      setCurrentPlanId(null); // Reset plan ID (not saved yet)
      toast.success('✨ ' + t('mealPlanner.planCreatedSave'));
    } catch (error) {
      console.error('Error generating meal plan:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
      setProgress(0);
      setProgressStep('');
    }
  };

  const handleSavePlan = async () => {
    try {
      setLoading(true);
      
      const saveResponse = await axios.post(`${API}/meal-plans`, {
        days,
        diet_type: dietType || null,
        preferences: preferences || null,
        meal_plan: mealPlan.meal_plan,
        shopping_list: mealPlan.shopping_list,
        prep_tips: mealPlan.prep_tips,
        total_calories: mealPlan.total_calories,
        total_protein: mealPlan.total_protein
      });
      
      setCurrentPlanId(saveResponse.data.id);
      await fetchSavedPlans();
      toast.success(t('mealPlanner.planSaved'));
    } catch (error) {
      console.error('Error saving plan:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <NavigationMenu />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="flex justify-center items-center gap-3 mb-4">
            <Calendar className="w-12 h-12 text-orange-600" />
          </div>
          <h1 className="text-5xl font-bold text-amber-900 mb-4">{t('mealPlanner.title')}</h1>
          <p className="text-xl text-gray-600">{t('mealPlanner.description')}</p>
        </div>

        {!mealPlan && !showHistory ? (
          <Card className="p-8 border-2 border-orange-100 shadow-xl max-w-2xl mx-auto">
            {/* Button to show history */}
            <div className="mb-6 flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowHistory(true)}
                className="border-orange-300 hover:bg-orange-50"
              >
                <History className="w-4 h-4 mr-2" />
                {t('mealPlanner.myPlans')} ({savedPlans.length})
              </Button>
            </div>
            
            <div className="space-y-6">
              <div>
                <Label className="text-lg font-semibold text-amber-900 mb-2 block">{t('mealPlanner.days')}</Label>
                <Select value={days.toString()} onValueChange={(v) => setDays(parseInt(v))}>
                  <SelectTrigger data-testid="days-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[3, 5, 7, 14].map((d) => (
                      <SelectItem key={d} value={d.toString()}>{d} {t('mealPlanner.daysLabel')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-lg font-semibold text-amber-900 mb-2 block">{t('mealPlanner.dietType')}</Label>
                <Select value={dietType || "none"} onValueChange={(v) => setDietType(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="diet-select">
                    <SelectValue placeholder={t('common.none')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('common.none')}</SelectItem>
                    {dietTypes.map((diet) => (
                      <SelectItem key={diet} value={diet}>{diet}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-lg font-semibold text-amber-900 mb-2 block">{t('mealPlanner.preferences')}</Label>
                <Textarea
                  data-testid="preferences-input"
                  value={preferences}
                  onChange={(e) => setPreferences(e.target.value)}
                  placeholder={t('mealPlanner.preferencesPlaceholder')}
                  className="min-h-24"
                />
              </div>

              <div>
                <Button
                  data-testid="generate-plan-button"
                  onClick={handleGenerate}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white py-6 text-lg"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>{progress}%</span>
                    </div>
                  ) : (
                    <>
                      <Calendar className="w-5 h-5 mr-2" />
                      {t('mealPlanner.generatePlan')}
                    </>
                  )}
                </Button>
                
                {/* Progress Bar */}
                {loading && (
                  <div className="mt-4">
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div 
                        className="bg-gradient-to-r from-orange-600 to-amber-600 h-3 transition-all duration-500 ease-out"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className="text-sm text-gray-600 mt-2 text-center">{progressStep}</p>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ) : showHistory ? (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-bold text-amber-900">{t('mealPlanner.myPlans')}</h2>
              <Button onClick={() => setShowHistory(false)} variant="outline">
                {t('mealPlanner.backToPlanner')}
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {savedPlans.map(plan => {
                const isMyPlan = plan.user_id === user?.id;
                
                return (
                  <Card key={plan.id} className={`p-6 border-2 hover:shadow-lg transition-shadow ${
                    isMyPlan ? 'border-orange-100' : 'border-green-100 bg-green-50/30'
                  }`}>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-lg text-amber-900">{plan.title || `${t('mealPlanner.planOf')} ${plan.days} ${t('mealPlanner.daysLabel')}`}</h3>
                          {isMyPlan ? (
                            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">
                              {t('mealPlanner.myPlan')}
                            </span>
                          ) : (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {plan.user_name}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {new Date(plan.created_at).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                      {isMyPlan && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeletePlan(plan.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  
                  <div className="space-y-2 mb-4">
                    <p className="text-sm text-gray-700">
                      📅 {plan.days} dias | 🔥 {plan.total_calories} kcal | 🥩 {plan.total_protein}g proteína
                    </p>
                    {plan.diet_type && (
                      <p className="text-sm text-orange-600">{t('mealPlanner.diet')}: {plan.diet_type}</p>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleLoadPlan(plan.id)}
                      className="flex-1 bg-orange-600 hover:bg-orange-700"
                    >
                      {t('mealPlanner.viewPlan')}
                    </Button>
                    
                    {!isMyPlan && (
                      <Button
                        onClick={() => handleClonePlan(plan)}
                        variant="outline"
                        className="border-blue-300 hover:bg-blue-50 text-blue-600"
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        {t('mealPlanner.clonePlan')}
                      </Button>
                    )}
                  </div>
                  </Card>
                );
              })}
            </div>
            
            {savedPlans.length === 0 && (
              <Card className="p-12 text-center border-2 border-dashed border-gray-300">
                <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">{t('mealPlanner.noPlansSaved')}</p>
                <p className="text-gray-400 text-sm mt-2">{t('mealPlanner.createFirstPlan')}</p>
              </Card>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-3xl font-bold text-amber-900">{t('mealPlanner.yourPlan')}</h2>
                  {mealPlan.user_id !== user?.id && (
                    <span className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {t('mealPlanner.createdBy')} {mealPlan.user_name}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  🔥 {mealPlan.total_calories || 0} {t('mealPlanner.kcalPerWeek')} | 🥩 {mealPlan.total_protein || 0}{t('mealPlanner.proteinPerWeek')}
                </p>
              </div>
              
              {/* Botões de Ação - Responsivo */}
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 w-full">
                {/* Linha 1: Botões principais */}
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  {/* Save Button - SEMPRE visível quando há plano */}
                  {mealPlan && (
                    <Button 
                      onClick={handleSavePlan}
                      disabled={loading || !!currentPlanId}
                      className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto font-bold shadow-lg"
                    >
                      {currentPlanId ? (
                        <>✅ {t('mealPlanner.saved')}</>
                      ) : (
                        <>
                          <Plus className="w-5 h-5 mr-2" />
                          💾 {t('mealPlanner.save')}
                        </>
                      )}
                    </Button>
                  )}
                  
                  {/* Delete Button - Only show if saved AND is my plan */}
                  {currentPlanId && mealPlan.user_id === user?.id && (
                    <Button 
                      onClick={() => handleDeletePlan(currentPlanId)}
                      disabled={loading}
                      variant="outline"
                      className="border-red-300 hover:bg-red-50 text-red-600 w-full sm:w-auto"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t('mealPlanner.delete')}
                    </Button>
                  )}
                </div>
                
                {/* Linha 2: Botões secundários */}
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  {/* Clone Button - Only show for other people's plans */}
                  {mealPlan.user_id !== user?.id && (
                    <Button 
                      onClick={() => handleClonePlan(mealPlan)}
                      disabled={loading}
                      className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      {t('mealPlanner.clone')}
                    </Button>
                  )}
                  
                  {/* History */}
                  <Button 
                    onClick={() => setShowHistory(true)} 
                    variant="outline"
                    className="w-full sm:w-auto"
                  >
                    <History className="w-4 h-4 mr-2" />
                    {t('mealPlanner.history')}
                  </Button>
                
                  {/* Share */}
                  {currentPlanId && mealPlan.user_id === user?.id && (
                    <Button 
                      onClick={() => setShowShareDialog(true)} 
                      variant="outline"
                      className="border-green-300 hover:bg-green-50 w-full sm:w-auto"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      {t('mealPlanner.share')}
                    </Button>
                  )}
                  
                  {/* Print */}
                  <Button 
                    onClick={handlePrint} 
                    variant="outline"
                    className="border-blue-300 hover:bg-blue-50 w-full sm:w-auto"
                    disabled={!currentPlanId}
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    {t('mealPlanner.print')}
                  </Button>
                  
                  {/* New */}
                  <Button 
                    onClick={() => {
                      setMealPlan(null);
                      setCurrentPlanId(null);
                    }} 
                    variant="outline"
                    className="w-full sm:w-auto"
                  >
                    {t('mealPlanner.createNew')}
                  </Button>
                </div>
              </div>
            </div>

            <Tabs defaultValue="meals" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="meals">{t('mealPlanner.meals')}</TabsTrigger>
                <TabsTrigger value="shopping">{t('mealPlanner.shoppingList')}</TabsTrigger>
              </TabsList>

              <TabsContent value="meals" className="space-y-4">
                {mealPlan.meal_plan?.map((day, dayIndex) => (
                  <Card key={dayIndex} className="p-6 border-2 border-orange-100">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-2xl font-bold text-amber-900">{day.day}</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          🔥 {day.total_calories || 0} kcal | 🥩 {day.total_protein || 0}g proteína
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Breakfast */}
                      <div className="border rounded-lg p-3 bg-amber-50/30">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-semibold text-amber-700">{t('mealPlanner.breakfast')}</h4>
                          {mealPlan.user_id === user?.id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleEditMeal(dayIndex, 'breakfast', day.breakfast)}
                            >
                              <Edit2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                        <p className="text-gray-700 text-sm font-medium mb-1">{day.breakfast}</p>
                        {day.breakfast_ingredients && day.breakfast_ingredients.length > 0 && (
                          <ul className="text-xs text-gray-600 mb-2 space-y-0.5">
                            {day.breakfast_ingredients.map((ing, i) => (
                              <li key={i}>• {ing.quantity} de {ing.item}</li>
                            ))}
                          </ul>
                        )}
                        <p className="text-xs text-gray-500 font-medium">
                          {day.breakfast_calories || 0} kcal | {day.breakfast_protein || 0}g prot.
                        </p>
                      </div>
                      
                      {/* Lunch */}
                      <div className="border rounded-lg p-3 bg-orange-50/30">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-semibold text-amber-700">{t('mealPlanner.lunch')}</h4>
                          {mealPlan.user_id === user?.id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleEditMeal(dayIndex, 'lunch', day.lunch)}
                            >
                              <Edit2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                        <p className="text-gray-700 text-sm font-medium mb-1">{day.lunch}</p>
                        {day.lunch_ingredients && day.lunch_ingredients.length > 0 && (
                          <ul className="text-xs text-gray-600 mb-2 space-y-0.5">
                            {day.lunch_ingredients.map((ing, i) => (
                              <li key={i}>• {ing.quantity} de {ing.item}</li>
                            ))}
                          </ul>
                        )}
                        <p className="text-xs text-gray-500 font-medium">
                          {day.lunch_calories || 0} kcal | {day.lunch_protein || 0}g prot.
                        </p>
                      </div>
                      
                      {/* Dinner */}
                      <div className="border rounded-lg p-3 bg-amber-50/30">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-semibold text-amber-700">{t('mealPlanner.dinner')}</h4>
                          {mealPlan.user_id === user?.id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleEditMeal(dayIndex, 'dinner', day.dinner)}
                            >
                              <Edit2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                        <p className="text-gray-700 text-sm font-medium mb-1">{day.dinner}</p>
                        {day.dinner_ingredients && day.dinner_ingredients.length > 0 && (
                          <ul className="text-xs text-gray-600 mb-2 space-y-0.5">
                            {day.dinner_ingredients.map((ing, i) => (
                              <li key={i}>• {ing.quantity} de {ing.item}</li>
                            ))}
                          </ul>
                        )}
                        <p className="text-xs text-gray-500 font-medium">
                          {day.dinner_calories || 0} kcal | {day.dinner_protein || 0}g prot.
                        </p>
                      </div>
                    </div>
                    
                    {day.snacks && day.snacks.length > 0 && (
                      <div className="mt-4 border rounded-lg p-3 bg-green-50/30">
                        <h4 className="font-semibold text-amber-700 mb-2">{t('mealPlanner.snacks')}</h4>
                        <div className="space-y-2">
                          {day.snacks.map((snack, i) => (
                            <div key={i} className="text-sm">
                              <p className="text-gray-700 font-medium">{snack}</p>
                            </div>
                          ))}
                        </div>
                        {day.snacks_ingredients && day.snacks_ingredients.length > 0 && (
                          <ul className="text-xs text-gray-600 mt-2 space-y-0.5">
                            {day.snacks_ingredients.map((ing, i) => (
                              <li key={i}>• {ing.quantity} de {ing.item}</li>
                            ))}
                          </ul>
                        )}
                        <p className="text-xs text-gray-500 mt-2 font-medium">
                          {day.snacks_calories || 0} kcal | {day.snacks_protein || 0}g prot.
                        </p>
                      </div>
                    )}
                  </Card>
                ))}

                {mealPlan.prep_tips && (
                  <Card className="p-6 border-2 border-orange-100 bg-orange-50">
                    <h3 className="text-xl font-bold text-amber-900 mb-3 flex items-center gap-2">
                      <ChefHat className="w-5 h-5" />
                      {t('mealPlanner.prepTips')}
                    </h3>
                    <p className="text-gray-700">{mealPlan.prep_tips}</p>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="shopping">
                <Card className="p-6 border-2 border-orange-100">
                  <h3 className="text-2xl font-bold text-amber-900 mb-6 flex items-center gap-2">
                    <ShoppingCart className="w-6 h-6" />
                    {t('mealPlanner.shoppingList')}
                  </h3>
                  
                  {/* Add new item form - Only for my plans */}
                  {mealPlan.user_id === user?.id && (
                    <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm font-semibold text-green-900 mb-3">{t('mealPlanner.addItem')}</p>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                        <Input
                          placeholder={t('mealPlanner.item')}
                          value={newShoppingItem.item}
                          onChange={(e) => setNewShoppingItem(prev => ({ ...prev, item: e.target.value }))}
                          className="col-span-2"
                        />
                        <Input
                          placeholder={t('mealPlanner.quantity')}
                          value={newShoppingItem.quantity}
                          onChange={(e) => setNewShoppingItem(prev => ({ ...prev, quantity: e.target.value }))}
                        />
                        <Button onClick={handleAddShoppingItem} size="sm" className="bg-green-600 hover:bg-green-700">
                          <Plus className="w-4 h-4 mr-1" />
                          {t('mealPlanner.add')}
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {mealPlan.shopping_list && mealPlan.shopping_list.length > 0 ? (
                    <div className="space-y-2">
                      {mealPlan.shopping_list.map((item, index) => (
                        <div key={index} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="flex items-center gap-3 flex-1">
                            <input type="checkbox" className="w-5 h-5" />
                            <div>
                              <span className="font-medium">{item.item}</span>
                              <span className="text-gray-600 ml-2">({item.quantity})</span>
                              {item.category && (
                                <span className="text-xs text-orange-600 ml-2 bg-orange-100 px-2 py-0.5 rounded">
                                  {item.category}
                                </span>
                              )}
                            </div>
                          </div>
                          {mealPlan.user_id === user?.id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleRemoveShoppingItem(index)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600">{t('mealPlanner.noItemsInList')}</p>
                  )}
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        )}
        
        {/* Dialogs */}
        {editingMeal && (
          <EditMealDialog
            isOpen={!!editingMeal}
            onClose={() => setEditingMeal(null)}
            meal={editingMeal.currentMeal}
            mealType={editingMeal.mealType}
            onSave={handleSaveMeal}
          />
        )}
        
        <SharePlanDialog
          isOpen={showShareDialog}
          onClose={() => setShowShareDialog(false)}
          planId={currentPlanId}
        />
      </div>
    </div>
  );
};

export default MealPlannerPage;